import { PopoverAlign, PopoverSide } from './types';

const GAP = 12; // space between target edge and popover
const MARGIN = 8; // minimum distance from viewport edge

export type ComputedPlacement = {
  top: number;
  left: number;
  side: PopoverSide;
};

export function computePlacement(
  target: DOMRect,
  popoverWidth: number,
  popoverHeight: number,
  preferredSide: PopoverSide = 'bottom',
  align: PopoverAlign = 'start',
): ComputedPlacement {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Available space on each side
  const space: Record<PopoverSide, number> = {
    top: target.top,
    bottom: vh - target.bottom,
    left: target.left,
    right: vw - target.right,
  };

  const needed: Record<PopoverSide, number> = {
    top: popoverHeight + GAP,
    bottom: popoverHeight + GAP,
    left: popoverWidth + GAP,
    right: popoverWidth + GAP,
  };

  // Try preferred side first, then fall back to whichever has the most room
  const order: PopoverSide[] = [
    preferredSide,
    ...(['top', 'bottom', 'left', 'right'] as PopoverSide[]).filter((s) => s !== preferredSide),
  ].sort((a, b) => (a === preferredSide ? -1 : b === preferredSide ? 1 : space[b] - space[a]));

  const side = order.find((s) => space[s] >= needed[s]) ?? preferredSide;

  // Primary axis (along the side direction)
  let top: number;
  let left: number;

  if (side === 'top') {
    top = target.top - popoverHeight - GAP;
  } else if (side === 'bottom') {
    top = target.bottom + GAP;
  } else {
    // left / right: align vertically
    if (align === 'start') top = target.top;
    else if (align === 'end') top = target.bottom - popoverHeight;
    else top = target.top + target.height / 2 - popoverHeight / 2;
  }

  if (side === 'left') {
    left = target.left - popoverWidth - GAP;
  } else if (side === 'right') {
    left = target.right + GAP;
  } else {
    // top / bottom: align horizontally
    if (align === 'start') left = target.left;
    else if (align === 'end') left = target.right - popoverWidth;
    else left = target.left + target.width / 2 - popoverWidth / 2;
  }

  // Clamp to viewport
  top = Math.max(MARGIN, Math.min(top, vh - popoverHeight - MARGIN));
  left = Math.max(MARGIN, Math.min(left, vw - popoverWidth - MARGIN));

  return { top, left, side };
}
